import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
      borderColor: 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12)',
    }}
  >
    {children}
  </div>
);

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<'rider' | 'driver'>('rider');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, userType }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all';

  if (submitted) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <GlassCard className="p-8 sm:p-10 text-center">
            <div className="text-5xl mb-4">📧</div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Check Your Email</h2>
            <p className="text-slate-500 mb-6">
              If an account exists with <strong className="text-slate-700">{email}</strong>, we've sent a password reset link. It expires in 1 hour.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Didn't receive it? Check your spam folder.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => { setSubmitted(false); setEmail(''); }}
                className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg hover:bg-slate-700 transition-all"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors py-2"
              >
                ← Back to Login
              </button>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <GlassCard className="p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-slate-800 mb-1">Forgot Password</h1>
            <p className="text-slate-500">We'll send you a reset link</p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-6 px-4 py-3 rounded-xl text-sm border text-rose-600"
              style={{
                background: 'rgba(255,241,242,0.8)',
                borderColor: 'rgba(254,205,211,0.5)',
              }}
            >
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Type Toggle */}
            <div className="flex gap-2 p-1 rounded-xl" style={{ background: 'rgba(241,245,249,0.6)' }}>
              {(['rider', 'driver'] as const).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setUserType(type)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 capitalize ${
                    userType === type
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {type === 'rider' ? '🚶 Rider' : '🚗 Driver'}
                </button>
              ))}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className={inputClass}
                placeholder="you@example.com"
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-300/40 hover:bg-slate-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
